import { useState, useEffect, createContext, useContext, useRef } from "react";
import { User, Session } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { useQueryClient } from "@tanstack/react-query";
import { generalReportException } from "@/lib/logs/reportExceptions";

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signOut: (reason?: string) => Promise<void>;
  showLogoutModal: boolean;
  setShowLogoutModal: (show: boolean) => void;
  logoutReason: string;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  session: null,
  loading: true,
  signOut: async () => {},
  showLogoutModal: false,
  setShowLogoutModal: () => {},
  logoutReason: "",
});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }

  return context;
};

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [logoutReason, setLogoutReason] = useState<string>("");
  const queryClient = useQueryClient();

  // Track if user was previously signed in to avoid clearing cache on token refresh
  const wasSignedInRef = useRef<boolean>(false);

  const cleanupAuthState = () => {
    Object.keys(localStorage).forEach((key) => {
      if (key.startsWith("supabase.auth.") || key.includes("sb-")) {
        localStorage.removeItem(key);
      }
    });
  };

  const signOut = async (reason?: string) => {
    // Eğer reason varsa, modal göster (otomatik logout)
    if (reason) {
      setLogoutReason(reason);
      setShowLogoutModal(true);
    }

    try {
      cleanupAuthState();
      await supabase.auth.signOut({ scope: "global" });
    } catch (error) {
      generalReportException(error as Error, { operation: "useAuth.signOut" });
    }
  };

  useEffect(() => {
    // Set up auth state listener first
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setSession(session);
        setUser(session?.user ?? null);
        setLoading(false);

        // Only clear React Query cache on ACTUAL new sign-in, not on token refresh
        // Token refresh also fires SIGNED_IN event, so we check if user was already signed in
        if (event === "SIGNED_IN") {
          if (!wasSignedInRef.current) {
            // This is an actual new sign-in, clear cache for fresh data
            queryClient.clear();
          }

          // Update ref to indicate user is now signed in
          wasSignedInRef.current = true;
          setShowLogoutModal(false); // Login olunca modal'ı kapat
        }

        // SIGNED_OUT event'i geldiğinde modal göster
        if (event === "SIGNED_OUT") {
          wasSignedInRef.current = false;
          setLogoutReason("Oturumunuz sonlandı. Güvenliğiniz için tekrar giriş yapmanız gerekiyor.");
          setShowLogoutModal(true);
        }

        // TOKEN_REFRESHED event'i başarısız olduğunda da göster
        if (event === "TOKEN_REFRESHED" && !session) {
          wasSignedInRef.current = false;
          setLogoutReason("Oturum süresi doldu. Lütfen tekrar giriş yapın.");
          setShowLogoutModal(true);
        }
      },
    );

    // Then check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);

      // If user already has a session, mark as signed in
      if (session?.user) {
        wasSignedInRef.current = true;
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const value = {
    user,
    session,
    loading,
    signOut,
    showLogoutModal,
    setShowLogoutModal,
    logoutReason,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
